import type { AlertRuleV2 } from '../api/alerts';
import type { ExchangeConnection, ExchangeFreshnessEntry } from '../api/exchange';
import type {
  DeliveryReadiness,
  NotificationChannelStatus,
  NotificationPreferenceMatrix,
} from '../api/notifications';
import type { CorrectiveRecallProof } from './correctiveRecall';
import type { SetupHandoffContext } from './setupHandoff';

export type MonitoringLoopCtaKey =
  | 'review-drift'
  | 'set-targets-first'
  | 'review-largest-drifts'
  | 'review-suggested-trades'
  | 'check-analytics'
  | 'open-automation'
  | 'review-active-rules'
  | 'enable-alerts'
  | 'verify-notification-readiness';

export interface MonitoringLoopProofItem {
  label: string;
  value: string;
}

export interface MonitoringLoopDecision {
  key: MonitoringLoopCtaKey;
  tone: 'info' | 'success' | 'warning';
  title: string;
  message: string;
  proof: MonitoringLoopProofItem[];
  actionPath?: string | null;
  isCorrectiveRecall?: boolean;
}

export interface NotificationReadinessSummary {
  hasGap: boolean;
  tone: 'success' | 'warning';
  title: 'Notification readiness confirmed' | 'Verify Notification Readiness';
  message: string;
  proof: MonitoringLoopProofItem[];
  needsPortfolioDriftPreference: boolean;
  needsPushSubscription: boolean;
  needsVerifiedEmail: boolean;
  needsDigestReadiness: boolean;
  primaryBlocker: 'portfolioDrift' | 'push' | 'email' | 'digest' | null;
}

export interface AlertCoverageSummary {
  state: 'empty' | 'low' | 'ready';
  coveredLabels: string[];
  missingLabels: string[];
  enabledRuleCount: number;
}

interface MonitoringLoopInputs {
  setupHandoff: SetupHandoffContext | null;
  hasPortfolioSnapshot: boolean;
  hasAnalyticsData: boolean;
  exchangeConnections: ExchangeConnection[];
  staleConnections: ExchangeFreshnessEntry[];
  activeTargetCount: number;
  driftedAssetCount: number;
  activeGoalName?: string | null;
  alertRulesCount: number | null;
  enabledAlertRulesCount: number | null;
  automationRulesCount: number | null;
  activeAutomationRulesCount: number | null;
  driftCoverageActive: boolean | null;
  notificationReadiness: NotificationReadinessSummary | null;
  recalledCorrectiveProof?: CorrectiveRecallProof | null;
}

export interface MonitoringHandoffContext {
  source: 'dashboard' | 'portfolio-analytics' | 'goals';
  destination: 'alerts' | 'automation' | 'notifications';
  activeGoalName: string;
  activeTargetCount: number;
  driftedAssetCount: number;
  proofLabel: string;
  proofOccurredAt?: string | null;
  recommendedRuleType?: 'position_change';
  blocker?: 'portfolioDrift' | 'push' | 'email' | 'digest';
  ruleFamily?: 'portfolio-drift' | 'portfolio-watch' | 'risk-watch';
}

function pluralize(count: number, singular: string, plural = `${singular}s`) {
  return count === 1 ? singular : plural;
}

function getSetupProofLabel(setupHandoff: SetupHandoffContext | null) {
  if (!setupHandoff) return null;

  switch (setupHandoff.kind) {
    case 'csv-import':
      return setupHandoff.sourceLabel;
    case 'api-sync':
      return setupHandoff.exchangeLabel;
    case 'goal-created':
    case 'goal-updated':
    case 'goal-activated':
    case 'goal-deleted':
      return setupHandoff.goalName;
    default:
      return null;
  }
}

function getMonitoringProofLabel(
  setupHandoff: SetupHandoffContext | null,
  activeGoalName?: string | null,
) {
  return getSetupProofLabel(setupHandoff) ?? activeGoalName ?? 'Active goal';
}

export function isDriftCapableAlertRule(rule: AlertRuleV2) {
  return rule.type === 'concentration_warning' || rule.type === 'position_change';
}

function getExchangeProofCounts(
  exchangeConnections: ExchangeConnection[],
  staleConnections: ExchangeFreshnessEntry[],
) {
  const staleIds = new Set(staleConnections.map((entry) => entry.connectionId));

  let synced = 0;
  let stale = 0;
  let blocked = 0;
  let notYetProven = 0;

  for (const connection of exchangeConnections) {
    if (connection.status === 'error') {
      blocked += 1;
      continue;
    }

    if (staleIds.has(connection.id) && connection.last_synced_at) {
      stale += 1;
      continue;
    }

    if (connection.status === 'active' && !connection.last_synced_at) {
      notYetProven += 1;
      continue;
    }

    if (connection.last_synced_at) {
      synced += 1;
    }
  }

  return { synced, stale, blocked, notYetProven };
}

export function getNotificationReadinessSummary({
  preferences,
  channelStatus,
  weeklyDigestEnabled,
  digestReadiness,
}: {
  preferences: NotificationPreferenceMatrix | null;
  channelStatus: NotificationChannelStatus | null;
  weeklyDigestEnabled: boolean | null;
  digestReadiness: DeliveryReadiness | null;
}): NotificationReadinessSummary | null {
  if (!preferences || !channelStatus || weeklyDigestEnabled === null || digestReadiness === null) {
    return null;
  }

  const rows = Object.values(preferences);
  const pushToggleCount = rows.filter((row) => row.push).length;
  const emailToggleCount = rows.filter((row) => row.email).length;
  const savedPreferenceCount = rows.filter((row) => row.inApp || row.push || row.email).length;
  const hasPortfolioDriftPreference =
    preferences.portfolioDrift.inApp || preferences.portfolioDrift.push || preferences.portfolioDrift.email;

  const needsPortfolioDriftPreference = !hasPortfolioDriftPreference;
  const needsPushSubscription = pushToggleCount > 0 && channelStatus.pushReadiness !== 'subscribed';
  const needsVerifiedEmail = emailToggleCount > 0 && channelStatus.emailReadiness !== 'verified';
  const needsDigestReadiness = weeklyDigestEnabled && digestReadiness !== 'ready';
  const hasGap =
    needsPortfolioDriftPreference || needsPushSubscription || needsVerifiedEmail || needsDigestReadiness;
  const primaryBlocker = needsPortfolioDriftPreference
    ? 'portfolioDrift'
    : needsPushSubscription
      ? 'push'
      : needsVerifiedEmail
        ? 'email'
        : needsDigestReadiness
          ? 'digest'
          : null;

  if (!hasGap) {
    return {
      hasGap: false,
      tone: 'success',
      title: 'Notification readiness confirmed',
      message:
        savedPreferenceCount > 0 || weeklyDigestEnabled
          ? 'Saved alert channels and digest delivery are ready for repeat monitoring.'
          : 'Notification delivery is ready when you decide to add alert or digest coverage.',
      proof: [
        { label: 'Saved rows', value: savedPreferenceCount.toString() },
        { label: 'Portfolio Drift', value: 'saved' },
        { label: 'Push subscriptions', value: channelStatus.pushSubscriptionCount.toString() },
        { label: 'Email', value: channelStatus.emailReadiness === 'verified' ? 'verified' : 'not selected' },
        { label: 'Digest', value: weeklyDigestEnabled ? 'ready' : 'off' },
      ],
      needsPortfolioDriftPreference: false,
      needsPushSubscription: false,
      needsVerifiedEmail: false,
      needsDigestReadiness: false,
      primaryBlocker: null,
    };
  }

  let message = 'Saved preferences exist, but delivery still needs proof on this surface.';
  if (needsPortfolioDriftPreference) {
    message =
      'Alert coverage exists, but Portfolio Drift delivery is not saved yet. Choose at least one Portfolio Drift channel before you rely on monitoring.';
  } else if (needsPushSubscription && needsVerifiedEmail) {
    message =
      'Saved alert preferences exist, but push is not subscribed on this device and email delivery is not verified yet.';
  } else if (needsPushSubscription) {
    message =
      'Saved push preferences exist, but this device is not yet subscribed for alert delivery.';
  } else if (needsVerifiedEmail && needsDigestReadiness) {
    message =
      'Saved email preferences and digest settings exist, but email delivery is not verified yet.';
  } else if (needsVerifiedEmail) {
    message =
      'Saved email preferences exist, but email delivery is not verified yet.';
  } else if (needsDigestReadiness) {
    message =
      'The weekly digest is saved, but delivery is not ready until email readiness is restored.';
  }

  return {
    hasGap: true,
    tone: 'warning',
    title: 'Verify Notification Readiness',
    message,
    proof: [
      { label: 'Portfolio Drift', value: hasPortfolioDriftPreference ? 'saved' : 'missing' },
      { label: 'Push rows', value: pushToggleCount.toString() },
      { label: 'Email rows', value: emailToggleCount.toString() },
      { label: 'Push state', value: channelStatus.pushReadiness },
      { label: 'Email state', value: channelStatus.emailReadiness },
      { label: 'Digest', value: weeklyDigestEnabled ? digestReadiness : 'off' },
    ],
    needsPortfolioDriftPreference,
    needsPushSubscription,
    needsVerifiedEmail,
    needsDigestReadiness,
    primaryBlocker,
  };
}

export function getAlertCoverageSummary(rules: AlertRuleV2[]): AlertCoverageSummary {
  const enabledRules = rules.filter((rule) => rule.enabled);

  if (enabledRules.length === 0) {
    return {
      state: 'empty',
      coveredLabels: [],
      missingLabels: ['Price', 'Drift', 'DeFi health'],
      enabledRuleCount: 0,
    };
  }

  const covered = new Set<string>();
  for (const rule of enabledRules) {
    if (
      rule.type === 'price_change' ||
      rule.type === 'volatility_spike' ||
      rule.type === 'loss_threshold' ||
      rule.type === 'gain_threshold'
    ) {
      covered.add('Price');
    }

    if (rule.type === 'concentration_warning' || rule.type === 'position_change') {
      covered.add('Drift');
    }

    if (rule.type === 'defi_risk') {
      covered.add('DeFi health');
    }
  }

  const coveredLabels = Array.from(covered);
  const missingLabels = ['Price', 'Drift', 'DeFi health'].filter((label) => !covered.has(label));
  const state = enabledRules.length < 2 || coveredLabels.length < 2 ? 'low' : 'ready';

  return {
    state,
    coveredLabels,
    missingLabels,
    enabledRuleCount: enabledRules.length,
  };
}

export function selectMonitoringLoopDecision({
  setupHandoff,
  hasPortfolioSnapshot,
  hasAnalyticsData,
  exchangeConnections,
  staleConnections,
  activeTargetCount,
  driftedAssetCount,
  activeGoalName,
  alertRulesCount,
  enabledAlertRulesCount,
  automationRulesCount,
  activeAutomationRulesCount,
  driftCoverageActive,
  notificationReadiness,
  recalledCorrectiveProof,
}: MonitoringLoopInputs): MonitoringLoopDecision | null {
  if (recalledCorrectiveProof?.followUpKey && recalledCorrectiveProof.followUpLabel) {
    return {
      key: recalledCorrectiveProof.followUpKey,
      tone: recalledCorrectiveProof.tone,
      title: recalledCorrectiveProof.followUpLabel,
      message: recalledCorrectiveProof.message,
      proof: [
        { label: 'Proof', value: recalledCorrectiveProof.proofLabel },
        ...recalledCorrectiveProof.proofItems,
      ],
      actionPath: recalledCorrectiveProof.followUpPath,
      isCorrectiveRecall: true,
    };
  }

  const proofLabel = getMonitoringProofLabel(setupHandoff, activeGoalName);
  const exchangeProofCounts = getExchangeProofCounts(exchangeConnections, staleConnections);
  const recentSetup = setupHandoff?.kind === 'csv-import' || setupHandoff?.kind === 'api-sync';

  if (hasPortfolioSnapshot && activeTargetCount > 0 && automationRulesCount === 0 && driftedAssetCount === 0) {
    return {
      key: 'open-automation',
      tone: 'info',
      title: 'Open Automation',
      message:
        `${activeGoalName ?? 'Your active goal'} is active, but no advisory monitoring rule is watching it yet. Open Automation so Pinecurl can keep watching this portfolio between visits.`,
      proof: [
        { label: 'Active targets', value: activeTargetCount.toString() },
        { label: 'Outside tolerance', value: driftedAssetCount.toString() },
        ...(proofLabel ? [{ label: 'Recent proof', value: proofLabel }] : []),
      ],
    };
  }

  if (hasPortfolioSnapshot && activeTargetCount > 0 && driftCoverageActive && notificationReadiness?.hasGap) {
    return {
      key: 'verify-notification-readiness',
      tone: notificationReadiness.tone,
      title: 'Verify Notification Readiness',
      message: notificationReadiness.message,
      proof: [
        { label: 'Active targets', value: activeTargetCount.toString() },
        { label: 'Outside tolerance', value: driftedAssetCount.toString() },
        ...notificationReadiness.proof,
      ],
    };
  }

  if (hasPortfolioSnapshot && activeTargetCount > 0 && automationRulesCount !== null && automationRulesCount > 0 && driftedAssetCount === 0) {
    return {
      key: 'review-active-rules',
      tone: activeAutomationRulesCount && activeAutomationRulesCount > 0 ? 'success' : 'warning',
      title: 'Review active rules',
      message:
        activeAutomationRulesCount && activeAutomationRulesCount > 0
          ? 'Saved advisory monitoring is already covering this portfolio. Review active rules to confirm the latest proof and keep revisit coverage truthful.'
          : 'Saved advisory rules already exist for this portfolio, but none are actively monitoring right now. Review active rules before you rely on this saved coverage.',
      proof: [
        { label: 'Active targets', value: activeTargetCount.toString() },
        { label: 'Saved rules', value: automationRulesCount.toString() },
        { label: 'Active monitoring', value: (activeAutomationRulesCount ?? 0).toString() },
      ],
    };
  }

  if (hasPortfolioSnapshot && activeTargetCount > 0) {
    return {
      key: 'review-drift',
      tone: driftedAssetCount > 0 ? 'warning' : 'info',
      title: 'Review Drift',
      message:
        driftedAssetCount > 0
          ? `${driftedAssetCount} ${pluralize(driftedAssetCount, 'asset')} moved outside the saved tolerance bands, so drift review is the next truthful monitoring step.`
          : `Saved targets are active across ${activeTargetCount} ${pluralize(activeTargetCount, 'allocation')}. Review drift to confirm today's holdings still match that plan.`,
      proof: [
        { label: 'Active targets', value: activeTargetCount.toString() },
        { label: 'Outside tolerance', value: driftedAssetCount.toString() },
        ...(proofLabel ? [{ label: 'Recent proof', value: proofLabel }] : []),
      ],
    };
  }

  if (
    recentSetup ||
    hasAnalyticsData ||
    exchangeProofCounts.synced > 0 ||
    exchangeProofCounts.stale > 0 ||
    exchangeProofCounts.blocked > 0 ||
    exchangeProofCounts.notYetProven > 0
  ) {
    let message =
      'Recent portfolio proof is in place, so analytics is the next place to confirm performance and risk context.';
    let tone: MonitoringLoopDecision['tone'] = 'info';

    if (exchangeProofCounts.blocked > 0) {
      tone = 'warning';
      message = `${exchangeProofCounts.blocked} ${pluralize(exchangeProofCounts.blocked, 'exchange')} still ${exchangeProofCounts.blocked === 1 ? 'is' : 'are'} blocked, so analytics is the safest place to inspect the last trusted history before acting.`;
    } else if (exchangeProofCounts.stale > 0) {
      tone = 'warning';
      message = `${exchangeProofCounts.stale} ${pluralize(exchangeProofCounts.stale, 'exchange snapshot')} ${exchangeProofCounts.stale === 1 ? 'is' : 'are'} stale. Check analytics against the last trusted history before you depend on the next sync.`;
    } else if (setupHandoff?.kind === 'csv-import') {
      message = `${setupHandoff.sourceLabel} already proved a recent import. Check analytics next to confirm returns, volatility, and deeper history context.`;
    } else if (setupHandoff?.kind === 'api-sync') {
      message = `${setupHandoff.exchangeLabel} already proved a recent sync. Check analytics next to confirm what changed beyond the latest holdings snapshot.`;
    } else if (!hasAnalyticsData && hasPortfolioSnapshot) {
      message =
        'The dashboard snapshot is live, but deeper performance context is still the next proof gap. Check analytics next.';
    }

    return {
      key: 'check-analytics',
      tone,
      title: 'Check Analytics',
      message,
      proof: [
        { label: 'Synced', value: exchangeProofCounts.synced.toString() },
        { label: 'Stale', value: exchangeProofCounts.stale.toString() },
        { label: 'Blocked', value: exchangeProofCounts.blocked.toString() },
        ...(proofLabel ? [{ label: 'Recent proof', value: proofLabel }] : []),
      ],
    };
  }

  if (enabledAlertRulesCount !== null && alertRulesCount !== null && enabledAlertRulesCount === 0) {
    return {
      key: 'enable-alerts',
      tone: alertRulesCount > 0 ? 'warning' : 'info',
      title: 'Enable Alerts',
      message:
        alertRulesCount > 0
          ? 'Alert rules exist, but none are active right now. Enable alerts so day-2 monitoring does not depend on manual refresh.'
          : 'No alert rules are covering price, drift, or DeFi follow-up yet. Enable alerts so repeat monitoring stays visible between visits.',
      proof: [
        { label: 'Saved rules', value: alertRulesCount.toString() },
        { label: 'Active rules', value: enabledAlertRulesCount.toString() },
        ...(proofLabel ? [{ label: 'Recent proof', value: proofLabel }] : []),
      ],
    };
  }

  if (notificationReadiness?.hasGap && enabledAlertRulesCount !== null && enabledAlertRulesCount > 0) {
    return {
      key: 'verify-notification-readiness',
      tone: notificationReadiness.tone,
      title: 'Verify Notification Readiness',
      message: notificationReadiness.message,
      proof: [
        { label: 'Active rules', value: enabledAlertRulesCount.toString() },
        ...notificationReadiness.proof,
      ],
    };
  }

  return null;
}

export function buildMonitoringHandoffContext({
  source,
  setupHandoff,
  activeTargetCount,
  driftedAssetCount,
  activeGoalName,
  proofOccurredAt,
  automationRulesCount,
  driftCoverageActive,
  notificationReadiness,
}: {
  source: MonitoringHandoffContext['source'];
  setupHandoff: SetupHandoffContext | null;
  activeTargetCount: number;
  driftedAssetCount: number;
  activeGoalName?: string | null;
  proofOccurredAt?: string | null;
  automationRulesCount: number | null;
  driftCoverageActive: boolean | null;
  notificationReadiness: NotificationReadinessSummary | null;
}): MonitoringHandoffContext | null {
  if (activeTargetCount <= 0 || !activeGoalName) {
    return null;
  }

  if (automationRulesCount === 0) {
    return {
      source,
      destination: source === 'goals' ? 'alerts' : 'automation',
      activeGoalName,
      activeTargetCount,
      driftedAssetCount,
      proofLabel: getMonitoringProofLabel(setupHandoff, activeGoalName),
      proofOccurredAt: proofOccurredAt ?? setupHandoff?.occurredAt ?? null,
      ruleFamily: source === 'goals' ? undefined : driftedAssetCount > 0 ? 'portfolio-drift' : 'portfolio-watch',
      recommendedRuleType: source === 'goals' ? 'position_change' : undefined,
    };
  }

  if (driftCoverageActive && notificationReadiness?.hasGap && notificationReadiness.primaryBlocker) {
    return {
      source,
      destination: 'notifications',
      activeGoalName,
      activeTargetCount,
      driftedAssetCount,
      proofLabel: getMonitoringProofLabel(setupHandoff, activeGoalName),
      proofOccurredAt: proofOccurredAt ?? setupHandoff?.occurredAt ?? null,
      blocker: notificationReadiness.primaryBlocker,
    };
  }

  return null;
}
